import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Stack,
  Tab,
  Tabs,
} from "@mui/material";
import ImageIcon from "@mui/icons-material/Image";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import LinkIcon from "@mui/icons-material/Link";
import UploadIcon from "@mui/icons-material/Upload";
import React, { useCallback, useEffect, useState } from "react";
import { CreativeCommonsTab } from "./tabs/CreativeCommonsTab";
import { AIImageTab } from "./tabs/AIImageTab";
import { URLImageTab } from "./tabs/URLImageTab";
import { UploadImageTab } from "./tabs/UploadImageTab";

type Props = {
  open: boolean;
  onClose: any;
  onInsertImage: (selected: { url: string; attribution?: string } | null) => void;
  defaultSearchValue?: string;
  initialImage?: { img: string; title: string; author: string };
  disableImageProxy?: boolean;
  enableRemoveImage?: boolean;
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`image-tabpanel-${index}`}
      aria-labelledby={`image-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  );
}

export const AddImageDialog = (props: Props) => {
  const { open = false, onClose, onInsertImage, defaultSearchValue, initialImage, enableRemoveImage } = props;
  const [tabValue, setTabValue] = useState(0);
  const [selectedImage, setSelectedImage] = useState<{ url: string; attribution?: string } | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedImage(null);
      if (initialImage) {
        setTabValue(2); // URL tab
        setSelectedImage({ url: initialImage.img });
      } else {
        setTabValue(0); // Creative Commons tab
      }
    }
  }, [open, initialImage]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setSelectedImage(null);
  };

  const handleSelectImage = useCallback((url: string, attribution?: string) => {
    if (url) {
      setSelectedImage({ url, attribution });
    } else {
      setSelectedImage(null);
    }
  }, []);

  const handleInsert = () => {
    if (selectedImage) {
      onInsertImage(selectedImage);
      onClose();
    }
  };

  const handleRemoveImage = () => {
    onInsertImage(null);
    onClose();
  };

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      open={open}
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          centered
          sx={{
            minHeight: 48,
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
              py: 1.25,
              fontSize: '0.9rem',
            }
          }}
        >
          <Tab icon={<ImageIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Search" />
          <Tab icon={<AutoAwesomeIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="AI Generate" />
          <Tab icon={<LinkIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="URL" />
          <Tab icon={<UploadIcon sx={{ fontSize: 20 }} />} iconPosition="start" label="Upload" />
        </Tabs>
      </Box>

      <DialogContent>
        <TabPanel value={tabValue} index={0}>
          <CreativeCommonsTab 
            onSelectImage={handleSelectImage}
            defaultSearch={defaultSearchValue}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <AIImageTab onSelectImage={handleSelectImage} />
        </TabPanel>
        <TabPanel value={tabValue} index={2}>
          <URLImageTab 
            onSelectImage={handleSelectImage}
            initialUrl={initialImage?.img}
          />
        </TabPanel>
        <TabPanel value={tabValue} index={3}>
          <UploadImageTab onSelectImage={handleSelectImage} />
        </TabPanel>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} sx={{ width: '100%' }}>
          <Box>
            {enableRemoveImage && selectedImage && (
              <Button
                variant="text"
                color="error"
                onClick={handleRemoveImage}
              >
                Remove Image
              </Button>
            )}
          </Box>

          <Stack direction="row" spacing={2}>
            <Button color="inherit" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleInsert}
              disabled={!selectedImage}
            >
              {tabValue === 0 ? "Insert Selected Image" : "Insert Image"}
            </Button>
          </Stack>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};